import { HTMLAttributes } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'

// evertel
import { Badge, Text, Row, Button, Icon } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { ProfilePic } from '@evertel/web/user'
import { DisplayThread } from '@evertel/stores'
import { Link, LinkProps } from 'react-router'


interface DrawerNavThreadLinkProps extends LinkProps {
    thread: DisplayThread
    selected?: boolean
    avatarWidth?: number
    unreadCount?: number
    hasUrgentUnreads?: string
    muted?: boolean
    onClick?: () => void
    onCloseThread?: (e: any) => Promise<void>
}


const DrawerNavThreadLink: React.FC<DrawerNavThreadLinkProps> = observer(({
    thread,
    to,
    onCloseThread,
    avatarWidth = 20,
    selected,
    unreadCount = 0,
    muted,
    hasUrgentUnreads,
    ...otherProps
}) => {

    const sessionService = useService(SessionService, [])

    const otherUserIds = thread.userIds?.filter(id => id !== sessionService.currentUserId)
    const isJustMe = thread.userIds?.length === 1 && thread.userIds[0] === sessionService.currentUserId

    // Show close button for non-bot threads that aren't just me
    const showCloseButton = !isJustMe && !thread.isBotThread && !!onCloseThread

    if (!thread.userIds?.length) return null

    const singleUserId = (isJustMe) ? thread.userIds[0] :
        (otherUserIds?.length === 1)
            ? otherUserIds[0]
            : undefined

    return (
        <div className="d-flex position-relative drawer-item-link-wrapper">
            <Link
                to={to}
                className={classNames('d-flex justify-content-between drawer-item-link', {
                    'hover-action': to,
                    'active': selected,
                    'muted': muted,
                    'unread': unreadCount
                })}
                {...otherProps}
                data-drawer-nav="thread"
            >
                <Row valign="center" style={{ width: 220 }} >
                    <div className="me-3">
                        {(singleUserId) ? (
                            <ProfilePic
                                userId={singleUserId}
                                width={avatarWidth}
                                height={avatarWidth}
                            />
                        ) : (
                            <Badge
                                color="muted"
                                className="d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                    width: avatarWidth,
                                    height: avatarWidth
                                }}>
                                {otherUserIds?.length}
                            </Badge>
                        )}
                    </div>
                    <Text
                        className="text-truncate"
                        color={(hasUrgentUnreads) ? 'danger' : undefined}
                    >
                        {thread.name} {isJustMe && <Text className="ms-2">(me)</Text>}
                    </Text>
                </Row>
                {(!!unreadCount) && (
                    <Row
                        align="right"
                        valign="center"
                        className="unread-badge">
                        <div>
                            <Badge color={(hasUrgentUnreads) ? 'danger' : 'warning'} title="Unread count">
                                <span className="visually-hidden">Unread count ${hasUrgentUnreads? 'with some urgent messages':''}:</span>
                                {unreadCount}
                            </Badge>
                        </div>
                    </Row>
                )}
            </Link>
            {(showCloseButton) && (
                <div
                    className="close-thread d-flex position-absolute end-0 align-items-center mx-2 h-100"
                >
                    <Button
                        className="p-1"
                        size="sm"
                        onClick={onCloseThread}
                        aria-label="Close thread"
                        data-drawer-nav="close"
                    >
                        <Icon
                            name="xmark"
                            style={{fontSize: '1.3em'}}
                        />
                    </Button>
                </div>
            )}
        </div>
    )
})

export { DrawerNavThreadLink }
